import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { useGetCasesQuery } from "@apis/cases/useCases"
import type { ICaseSearchParams } from "@apis/cases/types"
import { casesKeys } from "@apis/cases/queryKeys"
import { DataTable } from "@components/DataTable/DataTable"
import { PageTitle } from "@components/PageTitle"
import { Card, CardAction, CardHeader, CardTitle } from "@components/ui/card"
import { Badge } from "@components/ui/badge"
import { useDrawer } from "@contexts/DrawerContext"
import { usePaginationState } from "@hooks/usePaginationState"
import { useColumnConfig } from "@hooks/useColumnConfig"
import Case from "@pages/case"
import { casesColumns } from "./utils/tableColumnsSchema"
import { useCasesFilters } from "./utils/useCasesFilters"

export const CasesV2 = () => {
  const queryClient = useQueryClient()
  const casesPagination = usePaginationState(0, 50)
  const { openDrawer, open } = useDrawer()
  const { casesFilters, filterConfig, casesApiParams } = useCasesFilters()
  const casesColumnConfig = useColumnConfig({
    tableId: "casesQueue",
    columns: casesColumns,
  })

  const apiParams: ICaseSearchParams = {
    ...casesApiParams,
    page: casesPagination.pagination.pageIndex + 1,
    per_page: casesPagination.pagination.pageSize,
  }

  const getCasesQuery = useGetCasesQuery(apiParams)

  useEffect(() => {
    if (!open) {
      queryClient.invalidateQueries({
        queryKey: casesKeys.lists(),
      })
    }
  }, [open, queryClient])

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <PageTitle title="Cases Queue" />
      <Card>
        <CardHeader>
          <CardTitle className="tw:flex tw:items-center tw:gap-2">
            <span>Total count:</span>
            {getCasesQuery.data && (
              <Badge>{getCasesQuery.data?.total_items}</Badge>
            )}
          </CardTitle>
          <CardAction>
            {getCasesQuery.isFetching && (
              <Loader2 className="tw:animate-spin" />
            )}
          </CardAction>
        </CardHeader>
      </Card>
      <DataTable
        pending={getCasesQuery.isLoading}
        columns={casesColumns}
        data={getCasesQuery.data?.data ?? []}
        filters={filterConfig}
        paginationType="server"
        {...casesPagination}
        {...casesFilters}
        rowCount={getCasesQuery.data?.total_items}
        onRowClick={(row) =>
          openDrawer(<Case caseGuid={row.case_guid} />, "Case")
        }
        showViewOptions
        {...casesColumnConfig}
        variant="contrast"
      />
    </div>
  )
}
